import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, doc, getDoc, getDocs, updateDoc, setDoc, increment } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordMatch = ({ userData, onBack, selectedLevel }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [gameLoading, setGameLoading] = useState(false)
  const [currentLevel, setCurrentLevel] = useState(selectedLevel) // Seçilen level'ı kullan
  const [currentSubLevel, setCurrentSubLevel] = useState(1) // Alt seviye (1.1, 1.2)
  const [userLevel, setUserLevel] = useState('A1') // Kullanıcının seviyesi (A1, A2, etc.)
  const [words, setWords] = useState([])
  const [gameWords, setGameWords] = useState([])
  const [selectedButton, setSelectedButton] = useState(null)
  const [selectedWord, setSelectedWord] = useState(null)
  const [matchedPairs, setMatchedPairs] = useState(0)
  const [lives, setLives] = useState(3)
  const [score, setScore] = useState(0)
  const [gameCompleted, setGameCompleted] = useState(false)
  const [showExitDialog, setShowExitDialog] = useState(false)
  const [showLevelProgressDialog, setShowLevelProgressDialog] = useState(false)
  const [showLevelCompleteDialog, setShowLevelCompleteDialog] = useState(false)

  useEffect(() => {
    if (user && userData && selectedLevel) {
      setUserLevel(userData.level || 'A1')
      setCurrentLevel(selectedLevel)
      loadWords()
    }
  }, [user, userData, selectedLevel])

  // selectedLevel değiştiğinde currentLevel'i güncelle
  useEffect(() => {
    if (selectedLevel) {
      setCurrentLevel(selectedLevel)
    }
  }, [selectedLevel])

  const loadWords = async () => {
    try {
      setLoading(true)
      console.log('Loading words for level:', userLevel, 'game level:', selectedLevel)

      if (!selectedLevel) {
        console.error('selectedLevel is undefined')
        toast.error('Level seçimi yapılmadı')
        return
      }

      // Android uygulamasındaki aynı yapıyı kullan
      const firestoreLevel = userLevel.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'wordpairs' : 'wordPairs'

      // Seçilen level'ın dokümanını çek
      const levelDocRef = doc(db, 'words', firestoreLevel, 'levels', selectedLevel.toString())
      const levelDoc = await getDoc(levelDocRef)

      if (!levelDoc.exists()) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      const data = levelDoc.data()
      const levelWords = data[fieldName] || []

      if (levelWords.length === 0) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      console.log('Level words:', levelWords.length)
      setWords(levelWords)
      startGame(levelWords)
    } catch (error) {
      console.error('Error loading words:', error)
      toast.error('Kelimeler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const startGame = (wordList) => {
    if (!wordList || wordList.length === 0) {
      toast.error('Kelime bulunamadı')
      return
    }

    // İngilizce ve Türkçe kelimeleri ayır
    const englishWords = wordList.map(word => word.word)
    const turkishWords = wordList.map(word => word.meaning)

    // Kelimeleri karıştır
    const shuffledEnglish = [...englishWords].sort(() => Math.random() - 0.5)
    const shuffledTurkish = [...turkishWords].sort(() => Math.random() - 0.5)

    // Oyun kelimelerini oluştur
    const gameWordsList = []
    for (let i = 0; i < wordList.length; i++) {
      // İngilizce kelime için orijinal kelimeyi bul
      const englishOriginalIndex = englishWords.indexOf(shuffledEnglish[i])
      const englishOriginalWord = wordList[englishOriginalIndex]
      
      // Türkçe kelime için orijinal kelimeyi bul
      const turkishOriginalIndex = turkishWords.indexOf(shuffledTurkish[i])
      const turkishOriginalWord = wordList[turkishOriginalIndex]
      
      gameWordsList.push({
        id: `eng_${i}`,
        text: shuffledEnglish[i],
        type: 'english',
        matched: false,
        originalWord: englishOriginalWord
      })
      gameWordsList.push({
        id: `tr_${i}`,
        text: shuffledTurkish[i],
        type: 'turkish',
        matched: false,
        originalWord: turkishOriginalWord
      })
    }

    setGameWords(gameWordsList)
    setMatchedPairs(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
  }

  const handleButtonClick = (word) => {
    if (word.matched || gameLoading) return

    // Eğer aynı kart tekrar tıklanırsa seçimi iptal et
    if (selectedButton && selectedButton.id === word.id) {
      setSelectedButton(null)
      setSelectedWord(null)
      return
    }

    if (!selectedButton) {
      // İlk seçim
      setSelectedButton(word)
      setSelectedWord(word)
    } else {
      // İkinci seçim
      setGameLoading(true)
      
      // Eşleşme kontrolü
      console.log('Checking match:', {
        selected: { text: selectedWord.text, type: selectedWord.type, original: selectedWord.originalWord },
        current: { text: word.text, type: word.type, original: word.originalWord }
      })
      const isMatch = checkMatch(selectedWord, word)
      console.log('Match result:', isMatch)
      
      if (isMatch) {
        // Doğru eşleşme
        setMatchedPairs(prev => prev + 1)
        setScore(prev => prev + 10)
        
                 // Kelimeleri eşleştirilmiş olarak işaretle
         setGameWords(prev => prev.map(w => 
           w.id === selectedWord.id || w.id === word.id 
             ? { ...w, matched: true }
             : w
         ))
        
        // Oyun tamamlandı mı kontrol et
        if (matchedPairs + 1 >= words.length) {
          setTimeout(() => {
            handleLevelCompletion()
          }, 1000)
        }
      } else {
        // Yanlış eşleşme
        setLives(prev => prev - 1)
        setScore(prev => Math.max(0, prev - 20)) // Android'deki gibi -20 puan
                 toast.error('Yanlış!')
        
        // Oyun bitti mi kontrol et
        if (lives <= 1) {
          setTimeout(() => {
            gameOver()
          }, 1000)
        }
      }
      
      // Seçimleri sıfırla
      setTimeout(() => {
        setSelectedButton(null)
        setSelectedWord(null)
        setGameLoading(false)
      }, 1000)
    }
  }

  const checkMatch = (word1, word2) => {
    if (word1.type === word2.type) return false // Aynı türden kelimeler eşleşemez
    
    // İngilizce kelime ile Türkçe anlamını eşleştir
    if (word1.type === 'english' && word2.type === 'turkish') {
      return word1.originalWord.word === word2.originalWord.word &&
             word1.originalWord.meaning === word2.originalWord.meaning
    } else if (word1.type === 'turkish' && word2.type === 'english') {
      return word1.originalWord.word === word2.originalWord.word &&
             word1.originalWord.meaning === word2.originalWord.meaning
    }
    
    return false
  }

  const handleLevelCompletion = () => {
    if (currentSubLevel === 1) {
      // İlk alt seviye tamamlandı, ikinci alt seviyeye geç
      setShowLevelProgressDialog(true)
    } else if (currentSubLevel === 2) {
      // İkinci alt seviye de tamamlandı, level tamamen bitti
      setShowLevelCompleteDialog(true)
    }
  }

  const continueToNextSubLevel = () => {
    setShowLevelProgressDialog(false)
    setCurrentSubLevel(2)
    startGame(words) // Aynı kelimeleri farklı sırayla
  }

     const completeLevel = async () => {
     setShowLevelCompleteDialog(false)
     
     try {
       // Skoru Firebase'e kaydet - Uygulama ile aynı yapıyı kullan
       const firestoreLevel = userLevel.toUpperCase()
       
       // /gameProgress/A1/wordMatch/{userId}/completedLevels/level_{currentLevel}
       const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordMatch', user.uid, 'completedLevels', `level_${currentLevel}`)
       
       // Level'ı tamamlandı olarak işaretle
       await setDoc(levelDocRef, {
         completed: true,
         completedAt: new Date(),
         score: score
       })
       
       // Puanları da kaydet
       const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
       const userScoreDoc = await getDoc(userScoreRef)
       
       if (userScoreDoc.exists()) {
         const data = userScoreDoc.data()
         const completedLevels = data.completedLevels || []
         
         if (!completedLevels.includes(currentLevel)) {
           completedLevels.push(currentLevel)
         }
         
         await updateDoc(userScoreRef, {
           matchScore: increment(score),
           totalScore: increment(score),
           completedLevels: completedLevels
         })
       } else {
         await setDoc(userScoreRef, {
           username: userData?.username || user?.displayName || 'User',
           matchScore: score,
           totalScore: score,
           completedLevels: [currentLevel]
         })
       }
       
       
       
       console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
       
       toast.success(`Level ${currentLevel} tamamlandı! Skorunuz: ${score}`)
       
       // Bir sonraki level'e geç
       const nextLevelNumber = currentLevel + 1
       onBack({ page: 'word-match', selectedLevel: nextLevelNumber })
     } catch (error) {
       console.error('Error saving score:', error)
       toast.error('Skor kaydedilirken hata oluştu')
       // Hata olsa bile sonraki level'e geç
       const nextLevelNumber = currentLevel + 1
       onBack({ page: 'word-match', selectedLevel: nextLevelNumber })
     }
   }

   

  const gameOver = () => {
    toast.error('Oyun bitti! Canlarınız tükendi.')
    setGameCompleted(true)
  }

  const restartGame = () => {
    setCurrentSubLevel(1)
    startGame(words)
  }

  const nextLevel = () => {
    // Level seçim sayfasına geri dön
    onBack()
  }

  const exitGame = () => {
    setShowExitDialog(true)
  }

  const confirmExit = () => {
    setShowExitDialog(false)
    onBack()
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={exitGame}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Word Match
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={exitGame}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Word Match
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Level {currentLevel}.{currentSubLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Game Stats */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Skor: {score}
                </Typography>
                <Typography variant="h6">
                  Eşleşme: {matchedPairs}/{words.length}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {[1, 2, 3].map((heart) => (
                  <Box key={heart} sx={{ ml: 0.5 }}>
                    {heart <= lives ? (
                      <HeartIcon sx={{ color: 'red', fontSize: 24 }} />
                    ) : (
                      <HeartBorderIcon sx={{ color: 'gray', fontSize: 24 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Game Grid */}
        <Card sx={{ mx: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {gameWords.map((word) => (
                <Grid item xs={6} key={word.id}>
                  <Button
                    fullWidth
                    variant={word.matched ? "contained" : "outlined"}
                    color={word.matched ? "success" : "primary"}
                    disabled={word.matched || gameLoading}
                    onClick={() => handleButtonClick(word)}
                                         sx={{
                       height: 60,
                       fontSize: '0.9rem',
                       textTransform: 'none',
                                               backgroundColor: word.matched 
                          ? '#4caf50' 
                          : (selectedButton && selectedButton.id === word.id) 
                          ? '#e3f2fd' 
                          : 'white',
                        color: word.matched 
                          ? 'white' 
                          : (selectedButton && selectedButton.id === word.id) 
                          ? '#1976d2' 
                          : '#1976d2',
                        border: word.matched 
                          ? 'none' 
                          : (selectedButton && selectedButton.id === word.id) 
                          ? '3px solid #1976d2' 
                          : '2px solid #1976d2',
                        transform: (selectedButton && selectedButton.id === word.id) 
                          ? 'translateY(-2px)' 
                          : 'none',
                        boxShadow: (selectedButton && selectedButton.id === word.id) 
                          ? '0 4px 8px rgba(25, 118, 210, 0.3)' 
                          : 'none',
                        transition: 'all 0.3s ease',
                        animation: word.matched ? 'pulse 0.6s ease-in-out' : 'none',
                        '@keyframes pulse': {
                          '0%': {
                            backgroundColor: '#4caf50',
                            transform: 'scale(1)'
                          },
                          '50%': {
                            backgroundColor: '#66bb6a',
                            transform: 'scale(1.05)'
                          },
                          '100%': {
                            backgroundColor: '#4caf50',
                            transform: 'scale(1)'
                          }
                        },
                       '&:hover': {
                         backgroundColor: word.matched 
                           ? '#4caf50' 
                           : (selectedButton && selectedButton.id === word.id) 
                           ? '#e3f2fd' 
                           : '#f3f8ff',
                         transform: (selectedButton && selectedButton.id === word.id) 
                           ? 'translateY(-2px)' 
                           : 'translateY(-1px)'
                       }
                     }}
                  >
                    {word.text}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Level Progress Dialog */}
        <Dialog open={showLevelProgressDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            🎉 Level {currentLevel}.1 Tamamlandı!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Skorunuz: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Harika! Şimdi Level {currentLevel}.2'ye geçelim.
                Aynı kelimeleri farklı sırayla eşleştirin.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="contained" 
              onClick={continueToNextSubLevel}
            >
              {currentLevel}.2'ye Devam Et
            </Button>
          </DialogActions>
        </Dialog>

        {/* Level Complete Dialog */}
        <Dialog open={showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            🏆 Level {currentLevel} Tamamlandı!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Final Skor: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Tebrikler! Level {currentLevel}'deki tüm kelimeleri başarıyla eşleştirdiniz.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                         <Button 
               variant="outlined" 
               onClick={async () => {
                 // Level'i ve puanları kaydet ve sonra geri dön
                 try {
                   const firestoreLevel = userLevel.toUpperCase()
                   const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordMatch', user.uid, 'completedLevels', `level_${currentLevel}`)
                   
                   await setDoc(levelDocRef, {
                     completed: true,
                     completedAt: new Date(),
                     score: score
                   })
                   
                   // Puanları da kaydet
                   const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                   const userScoreDoc = await getDoc(userScoreRef)
                   
                   if (userScoreDoc.exists()) {
                     const data = userScoreDoc.data()
                     const completedLevels = data.completedLevels || []
                     
                     if (!completedLevels.includes(currentLevel)) {
                       completedLevels.push(currentLevel)
                     }
                     
                     await updateDoc(userScoreRef, {
                       matchScore: increment(score),
                       totalScore: increment(score),
                       completedLevels: completedLevels
                     })
                   } else {
                     await setDoc(userScoreRef, {
                       username: userData?.username || user?.displayName || 'User',
                       matchScore: score,
                       totalScore: score,
                       completedLevels: [currentLevel]
                     })
                   }
                   
                   console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                   toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                 } catch (error) {
                   console.error('Error saving score:', error)
                   toast.error('Skor kaydedilirken hata oluştu')
                 }
                 // Level seçim sayfasına dön
                 onBack({ page: 'word-match-level-selection', userLevel: userLevel })
               }}
               sx={{ mr: 2 }}
             >
               Level Seçimi
             </Button>
            <Button 
              variant="contained" 
              onClick={completeLevel}
            >
              Sonraki Level
            </Button>
          </DialogActions>
        </Dialog>

        {/* Game Completed Dialog */}
        <Dialog open={gameCompleted && !showLevelProgressDialog && !showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {lives > 0 ? '🎉 Oyun Tamamlandı!' : '😔 Oyun Bitti'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Skorunuz: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {lives > 0 
                  ? `Tebrikler! ${words.length} kelimeyi başarıyla eşleştirdiniz.`
                  : 'Canlarınız tükendi. Tekrar deneyin!'
                }
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                         <Button 
               variant="outlined" 
               onClick={async () => {
                 // Eğer oyun başarıyla tamamlandıysa level'i ve puanları kaydet
                 if (lives > 0) {
                   try {
                     const firestoreLevel = userLevel.toUpperCase()
                     const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordMatch', user.uid, 'completedLevels', `level_${currentLevel}`)
                     
                     await setDoc(levelDocRef, {
                       completed: true,
                       completedAt: new Date(),
                       score: score
                     })
                     
                     // Puanları da kaydet
                     const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                     const userScoreDoc = await getDoc(userScoreRef)
                     
                     if (userScoreDoc.exists()) {
                       const data = userScoreDoc.data()
                       const completedLevels = data.completedLevels || []
                       
                       if (!completedLevels.includes(currentLevel)) {
                         completedLevels.push(currentLevel)
                       }
                       
                       await updateDoc(userScoreRef, {
                         matchScore: increment(score),
                         totalScore: increment(score),
                         completedLevels: completedLevels
                       })
                     } else {
                       await setDoc(userScoreRef, {
                         username: userData?.username || user?.displayName || 'User',
                         matchScore: score,
                         totalScore: score,
                         completedLevels: [currentLevel]
                       })
                     }
                     
                     console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                     toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                   } catch (error) {
                     console.error('Error saving score:', error)
                     toast.error('Skor kaydedilirken hata oluştu')
                   }
                 }
                 // Level seçim sayfasına dön
                 onBack({ page: 'word-match-level-selection', userLevel: userLevel })
               }}
               sx={{ mr: 2 }}
             >
               Level Seçimi
             </Button>
            <Button 
              variant="contained" 
              onClick={restartGame}
            >
              Tekrar Oyna
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exit Confirmation Dialog */}
        <Dialog open={showExitDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Oyundan Çık</DialogTitle>
          <DialogContent>
            <Typography>
              Oyundan çıkmak istediğinizden emin misiniz? İlerlemeniz kaydedilmeyecek.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExitDialog(false)}>
              İptal
            </Button>
            <Button onClick={confirmExit} color="error">
              Çık
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default WordMatch
